import {
	type CodeBlockType,
	type ControlCharsType,
	type KeywordsType,
	isControlChar,
	keywordsObject,
} from "@quickpoint.me/shared";
import { type ReactElement, useEffect, useMemo } from "react";
import {
	useRecoilCallback,
	useRecoilTransaction_UNSTABLE,
	useRecoilValue,
} from "recoil";
import type { PageElementData, TapIdContainer } from "../@types/viewerati";
import mapPageCode from "../lib/mapPageCode";
import {
	PageElementIsLoadedFamily,
	PageElementsFamily,
	PageIndex,
	RenderedPageElementsToDisplay,
	TapIdContainers,
	createLocation,
} from "../state/page";
import {
	AudioFileMaps,
	DataFileMaps,
	ImageMaps,
	ParsedCode,
} from "../state/selectors";
import { EditMode, ShowAllTapElements } from "../state/ui";

import { NeueroDivergentReader } from "../state/accessibility";
import { CurrentFilter } from "../state/filter";

export interface ElementProps {
	atomId: number;
	exiting?: true;
	isAutospaceElement?: boolean;
	pageIndex: number;
	type: KeywordsType | ControlCharsType | CodeBlockType;
}

function RenderPage({
	preRenderForAnimation,
	index,
}: {
	preRenderForAnimation?: boolean;
	index?: number;
}) {
	const audioFileMaps = useRecoilValue(AudioFileMaps);
	const currentFilter = useRecoilValue(CurrentFilter);
	const dataFileMaps = useRecoilValue(DataFileMaps);
	const editMode = useRecoilValue(EditMode);
	const imageMaps = useRecoilValue(ImageMaps);

	let pageIndex = useRecoilValue(PageIndex) + (preRenderForAnimation ? 1 : 0);
	if (index !== undefined && index !== null) pageIndex = index;
	const pageCode = useRecoilValue(ParsedCode)[pageIndex - 1];

	const neueroDivergentReader = useRecoilValue(NeueroDivergentReader);
	const showAllTapElements = useRecoilValue(ShowAllTapElements(pageIndex));

	const setPageElementsDataFamily = useRecoilTransaction_UNSTABLE(
		({ set }) =>
			(pageElements: PageElementData[]) => {
				for (const pageElement of pageElements) {
					const key = createLocation(pageIndex, pageElement.id);
					// //console.log(key, pageElement.type);
					set(PageElementsFamily(key), {
						pageIndex,
						atomId: pageElement.id,
						data: pageElement,
						key,
					});
				}
			},
	);

	const setRenderedPageElementsToDisplay = useRecoilCallback(
		({ set }): ((elements: ReactElement<ElementProps>[]) => void) =>
			(elements) => {
				if (elements) {
					set(RenderedPageElementsToDisplay(pageIndex), elements);
				}
			},
	);

	// groups elements between tap keywords into containers i.e. [[1,2],[3,4,5]]
	// in this case 1,2 appear on render, 3,4,5 appear on tap.
	const setTapIdContainers = useRecoilCallback(
		({ set }): ((containers: TapIdContainer[], idx: number) => void) =>
			(containers: TapIdContainer[], idx) => {
				set(TapIdContainers(idx), containers);
			},
		[],
	);

	const setIsLoadedState = useRecoilCallback(
		({ set }) =>
			(pageElements: PageElementData[]) => {
				for (const pageElement of pageElements) {
					const key = createLocation(pageIndex, pageElement.id);
					if (isControlChar(pageElement.type)) {
						set(PageElementIsLoadedFamily(key), true);
						continue;
					}

					// Check if the type is a recognized keyword before indexing keywordsObject
					if (pageElement.type in keywordsObject) {
						// We know pageElement.type is a valid KeywordsType here
						const keywordDefinition =
							keywordsObject[pageElement.type as KeywordsType];
						if (keywordDefinition && keywordDefinition.type === "loadable") {
							set(PageElementIsLoadedFamily(key), false);
						} else {
							set(PageElementIsLoadedFamily(key), true);
						}
					} else {
						// Handle types not in keywordsObject (e.g., CodeBlockType '```') - assume loaded
						set(PageElementIsLoadedFamily(key), true);
					}
				}
			},
	);

	const { pageElements, pageReactElements, tapIdContainer } = useMemo(() => {
		if (!pageCode) {
			return {
				pageElements: undefined,
				pageReactElements: undefined,
				tapIdContainer: undefined,
			};
		}
		return mapPageCode({
			pageCode,
			showAllTapElements,
			editMode,
			imageMaps,
			audioFileMaps,
			dataFileMaps,
			currentFilter,
			neueroDivergentReader,
			pageIndex,
		});
	}, [
		pageCode,
		showAllTapElements,
		editMode,
		imageMaps,
		audioFileMaps,
		dataFileMaps,
		currentFilter,
		neueroDivergentReader,
		pageIndex,
	]);
	// set all the recoil states in a useEffect.
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!pageCode) return;
		if (pageElements && pageReactElements && tapIdContainer) {
			setTapIdContainers(tapIdContainer, pageIndex);
			setPageElementsDataFamily(pageElements);
			setRenderedPageElementsToDisplay(pageReactElements);
			setIsLoadedState(pageElements);
		}
	}, [pageElements, pageReactElements, tapIdContainer, pageIndex, pageCode]);

	return <></>;
}

export default RenderPage;
